import React from 'react';
import { Link } from 'react-router-dom';
import type { Address, QuoteShippingAddressOption, QuoteShippingMethod } from '../types/myaccount';

interface QuoteShippingPanelProps {
    billingAddress: Address | null;
    billingAddresses: QuoteShippingAddressOption[];
    emptyMethodsMessage?: string | null;
    hasItems: boolean;
    loading: boolean;
    error: string | null;
    requiresShipping: boolean;
    onBillingAddressChange: (addressId: number) => void;
    onShippingAddressChange: (addressId: number) => void;
    onSelectRate: (rateId: string) => void;
    onUseBillingAsShippingChange: (useBillingAsShipping: boolean) => void;
    selectedBillingAddressId: number;
    selectedShippingAddressId: number;
    selectedShippingRateId: string;
    shippingAddress: Address | null;
    shippingAddresses: QuoteShippingAddressOption[];
    shippingMethods: QuoteShippingMethod[];
    useBillingAsShipping: boolean;
    disabled?: boolean;
}

const formatAddress = (address: Address | null): string[] => {
    if (!address) {
        return [];
    }

    const name = [address.first_name, address.last_name].filter(Boolean).join(' ').trim();
    const locality = [address.city, address.state, address.postcode].filter(Boolean).join(', ').trim();

    return [
        name,
        address.company || '',
        address.address_1 || '',
        address.address_2 || '',
        locality,
        address.country || '',
        address.phone || '',
    ].filter((value) => value !== '');
};

const formatCurrency = (amount: string | number | undefined): string => {
    const numericAmount = typeof amount === 'number' ? amount : parseFloat(String(amount || '0'));
    return `$${numericAmount.toFixed(2)}`;
};

const QuoteShippingPanel: React.FC<QuoteShippingPanelProps> = ({
    billingAddress,
    billingAddresses,
    emptyMethodsMessage = null,
    hasItems,
    loading,
    error,
    requiresShipping,
    onBillingAddressChange,
    onShippingAddressChange,
    onSelectRate,
    onUseBillingAsShippingChange,
    selectedBillingAddressId,
    selectedShippingAddressId,
    selectedShippingRateId,
    shippingAddress,
    shippingAddresses,
    shippingMethods,
    useBillingAsShipping,
    disabled = false,
}) => {
    const billingAddressLines = formatAddress(billingAddress);
    const addressLines = formatAddress(shippingAddress);
    const toggleDisabled = disabled || loading;

    return (
        <div className="quote-shipping-panel mb-4 pb-4 border-bottom">
            <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
                <div>
                    <h6 className="mb-1">Shipping</h6>
                    <p className="text-muted mb-0">
                        WooCommerce rates are calculated from the selected shipping address for this quote.
                    </p>
                </div>
                <Link to="/edit-address" className="btn btn-outline-secondary btn-sm">
                    <i className="fas fa-map-marker-alt me-2"></i>
                    Manage Addresses
                </Link>
            </div>

            <div className="row g-3 mb-3">
                <div className="col-lg-6">
                    <label className="form-label">Billing Address</label>
                    {billingAddresses.length > 0 ? (
                        <select
                            className="form-select"
                            value={String(selectedBillingAddressId)}
                            disabled={disabled || loading}
                            onChange={(event) => onBillingAddressChange(Number(event.currentTarget.value))}
                        >
                            {billingAddresses.map((address) => (
                                <option key={address.id} value={String(address.id)}>
                                    {address.label}
                                    {address.is_default ? ' (Default)' : ''}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <div className="alert alert-warning mb-0">
                            Add a billing address before editing this quote.
                        </div>
                    )}
                </div>
                <div className="col-lg-6">
                    <div className="border rounded p-3 h-100 bg-light">
                        <strong className="d-block mb-2">Billing to</strong>
                        {billingAddressLines.length > 0 ? (
                            billingAddressLines.map((line) => (
                                <div key={line}>{line}</div>
                            ))
                        ) : (
                            <div className="text-muted">Select a billing address to continue.</div>
                        )}
                    </div>
                </div>
            </div>

            <div className="mb-3">
                <label
                    className={`quote-shipping-panel__toggle${useBillingAsShipping ? ' is-active' : ''}${toggleDisabled ? ' is-disabled' : ''}`}
                    htmlFor="quote-use-billing-as-shipping"
                >
                    <input
                        id="quote-use-billing-as-shipping"
                        type="checkbox"
                        className="quote-shipping-panel__toggle-input"
                        checked={useBillingAsShipping}
                        disabled={toggleDisabled}
                        onChange={(event) => onUseBillingAsShippingChange(event.currentTarget.checked)}
                    />
                    <span className="quote-shipping-panel__toggle-switch" aria-hidden="true">
                        <span className="quote-shipping-panel__toggle-knob"></span>
                    </span>
                    <span className="quote-shipping-panel__toggle-copy">
                        <span className="quote-shipping-panel__toggle-title">Use billing address for shipping</span>
                        <span className="quote-shipping-panel__toggle-note">
                            Hide the separate shipping address selector and rate this quote from the billing address.
                        </span>
                    </span>
                </label>
            </div>

            {!useBillingAsShipping && (
                <div className="row g-3 mb-3">
                    <div className="col-lg-6">
                        <label className="form-label">Shipping Address</label>
                        {shippingAddresses.length > 0 ? (
                            <select
                                className="form-select"
                                value={String(selectedShippingAddressId)}
                                disabled={disabled || loading}
                                onChange={(event) => onShippingAddressChange(Number(event.currentTarget.value))}
                            >
                                {shippingAddresses.map((address) => (
                                    <option key={address.id} value={String(address.id)}>
                                        {address.label}
                                        {address.is_default ? ' (Default)' : ''}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <div className="alert alert-warning mb-0">
                                Add a shipping address before selecting a shipping method.
                            </div>
                        )}
                    </div>
                    <div className="col-lg-6">
                        <div className="border rounded p-3 h-100 bg-light">
                            <strong className="d-block mb-2">Shipping to</strong>
                            {addressLines.length > 0 ? (
                                addressLines.map((line) => (
                                    <div key={line}>{line}</div>
                                ))
                            ) : (
                                <div className="text-muted">Select a shipping address to continue.</div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {useBillingAsShipping && (
                <div className="row g-3 mb-3">
                    <div className="col-lg-12">
                        <div className="border rounded p-3 h-100 bg-light">
                            <strong className="d-block mb-2">Shipping to</strong>
                            {billingAddressLines.length > 0 ? (
                                billingAddressLines.map((line) => (
                                    <div key={line}>{line}</div>
                                ))
                            ) : (
                                <div className="text-muted">Select a billing address to continue.</div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {!hasItems ? (
                <div className="alert alert-info mb-0">
                    Add quote items to calculate shipping options.
                </div>
            ) : loading ? (
                <div className="alert alert-light border mb-0 d-flex align-items-center">
                    <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                    Loading shipping methods...
                </div>
            ) : error ? (
                <div className="alert alert-danger mb-0">{error}</div>
            ) : !requiresShipping ? (
                <div className="alert alert-info mb-0">
                    No shipping method is required for the current quote items.
                </div>
            ) : shippingMethods.length === 0 ? (
                <div className="alert alert-warning mb-0">
                    {emptyMethodsMessage || 'No standard shipping methods are currently available for the selected shipping address.'}
                </div>
            ) : (
                <div className="border rounded p-3">
                    <div className="text-success fw-semibold mb-2">Shipment 1</div>
                    {emptyMethodsMessage && (
                        <div className="alert alert-warning py-2 px-3 mb-3">
                            {emptyMethodsMessage}
                        </div>
                    )}
                    <div className="text-muted small mb-3">
                        Select the WooCommerce shipping option to apply to this quote.
                    </div>
                    <div className="d-flex flex-column gap-2">
                        {shippingMethods.map((method) => {
                            const optionId = `quote-shipping-rate-${method.rate_id.replace(/[^a-z0-9_-]/gi, '-')}`;
                            const isSelected = selectedShippingRateId === method.rate_id;
                            const taxAmount = parseFloat(method.total_tax || '0');

                            return (
                                <label
                                    key={method.rate_id}
                                    htmlFor={optionId}
                                    className={`border rounded px-3 py-2 ${isSelected ? 'border-primary bg-primary-subtle' : 'bg-white'}`}
                                >
                                    <div className="d-flex justify-content-between align-items-start gap-3">
                                        <div className="d-flex align-items-start gap-2">
                                            <input
                                                id={optionId}
                                                type="radio"
                                                className="form-check-input mt-1"
                                                name="quote_shipping_rate_id"
                                                value={method.rate_id}
                                                checked={isSelected}
                                                disabled={disabled}
                                                onChange={() => onSelectRate(method.rate_id)}
                                            />
                                            <div>
                                                <div className="fw-semibold">{method.method_title}</div>
                                                <div className="small text-muted">
                                                    {method.method_id}
                                                    {taxAmount > 0 && ` | Tax ${formatCurrency(method.total_tax)}`}
                                                </div>
                                            </div>
                                        </div>
                                        <strong className={isSelected ? 'text-primary' : ''}>
                                            {formatCurrency(method.total)}
                                        </strong>
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuoteShippingPanel;
